/**
 * TouchTexture. Maintains an unsigned-byte RGBA DataTexture (R = heat) updated each
 * frame via stamp() + decay(). Never touches AudioContext or WebGL directly —
 * just writes Uint8Array data and sets needsUpdate.
 */

import * as THREE from 'three';

// ─── Constants ─────────────────────────────────────────────────────────────

/** Gaussian falloff radius in pixels for a single stamp. */
const STAMP_RADIUS = 10;

// ─── TouchTexture ───────────────────────────────────────────────────────────

export class TouchTexture {
  /** Public RGBA unsigned-byte DataTexture, uploaded to GPU via needsUpdate. */
  readonly texture: THREE.DataTexture;

  /** Raw Uint8Array backing the texture; exposed so tests can read R-channel values. */
  readonly data: Uint8Array;

  private readonly size: number;

  constructor(size: number) {
    this.size = size;
    this.data = new Uint8Array(size * size * 4);
    this.texture = new THREE.DataTexture(
      this.data as unknown as BufferSource,
      size,
      size,
      THREE.RGBAFormat,
      THREE.UnsignedByteType,
    );
    this.texture.needsUpdate = true;
  }

  // ─── Write ──────────────────────────────────────────────────────────────

  /**
   * Stamp a soft radial kernel at normalized (x, y) with the given intensity
   * into the R channel. Coordinates are clamped to [0, 1].
   */
  stamp(x: number, y: number, intensity: number): void {
    const s = this.size;
    const strength = Math.min(Math.max(intensity, 0), 1) * 255;
    // Convert screen-space coords to texture coords. Pointer Y is top-origin,
    // while shader UV Y is bottom-origin.
    const nx = Math.min(Math.max(x, 0), 1);
    const ny = Math.min(Math.max(y, 0), 1);
    const cx = Math.round(nx * (s - 1));
    const cy = Math.round((1 - ny) * (s - 1));

    const r = STAMP_RADIUS;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const px = cx + dx;
        const py = cy + dy;
        if (px < 0 || px >= s || py < 0 || py >= s) continue;
        // Gaussian falloff: e^(-d²/2σ²) with σ = r/2
        const dist2 = dx * dx + dy * dy;
        const sigma = r / 2;
        const weight = Math.exp(-dist2 / (2 * sigma * sigma));
        const idx = (py * s + px) * 4;
        this.data[idx] = Math.min(255, this.data[idx] + Math.round(weight * strength));
      }
    }
  }

  /**
   * Decay every R-channel sample toward zero. Low residual heat is cut off so
   * touched pixels cannot keep a permanent shader effect after release.
   * Sets texture.needsUpdate so three.js re-uploads on next render.
   */
  decay(factor: number): void {
    const len = this.data.length;
    for (let i = 0; i < len; i += 4) {
      const current = this.data[i];
      const next = Math.round(current * factor);
      this.data[i] = next < 2 ? 0 : Math.min(next, current - 1);
    }
    this.texture.needsUpdate = true;
  }

  /**
   * Zero the entire buffer (all channels). Useful for hard resets.
   */
  clear(): void {
    this.data.fill(0);
    this.texture.needsUpdate = true;
  }
}
